// LLM generated - Model Claude Opus 4
/**
 * useContestChatUtils - Shared utilities for contest chat state management
 *
 * Contains:
 * - Shared types for optimistic updates
 * - Reducer function for optimistic message updates
 * - Query key helpers
 * - Cache utility functions
 */
import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { ChatMessage, ChatMessagesInfiniteData } from '~/services/live-activity/types';
import type { ChatMode } from '~/services/live-activity/types/pusher-events';

import { createQueryKey, MESSAGE_PAGE_SIZE } from '../constants/queryKeys';

// --- Shared Types ---

export type MessageAction = {
  type: 'add';
  message: ChatMessage;
};

export type PendingOptimisticMessage = {
  content: string;
  timestamp: number;
  chatMode: ChatMode;
};

export type PendingOptimisticReaction = {
  messageId: string;
  code: string;
  action: 'add' | 'remove';
  timestamp: number;
};

// --- Reducer for Optimistic Updates ---

/**
 * Reducer for applying optimistic message updates.
 * Prepends new messages to the list.
 */
export function applyOptimisticMessage(
  messages: ChatMessage[],
  action: MessageAction
): ChatMessage[] {
  if (action.type === 'add') {
    return [action.message, ...messages];
  }
  return messages;
}

// --- Hook for Shared Refs and Utilities ---

type UseContestChatUtilsProps = {
  messagesChatId?: string;
  postsChatId?: string;
};

/**
 * Hook that provides shared refs and utility functions for chat state management.
 * Used by both messages and posts state hooks.
 */
export function useContestChatUtils({ messagesChatId, postsChatId }: UseContestChatUtilsProps) {
  const queryClient = useQueryClient();

  // Shared refs for tracking pending optimistic updates
  const pendingOptimisticMessagesRef = useRef<Map<string, PendingOptimisticMessage>>(new Map());
  const pendingOptimisticReactionsRef = useRef<Map<string, PendingOptimisticReaction>>(new Map());

  // Ref for the expectEvent function from Pusher hook
  const expectEventRef = useRef<((eventType: string, chatMode: ChatMode) => void) | null>(null);

  // Memoized query keys
  const messagesQueryKey = createQueryKey.chatMessages(messagesChatId, MESSAGE_PAGE_SIZE, false);
  const postsQueryKey = createQueryKey.chatMessages(postsChatId, MESSAGE_PAGE_SIZE, false);

  /**
   * Get the appropriate query key based on chat mode.
   */
  const getQueryKeyForChatMode = useCallback(
    (chatMode: ChatMode) => (chatMode === 'bidirectional' ? messagesQueryKey : postsQueryKey),
    [messagesQueryKey, postsQueryKey]
  );

  /**
   * Check if a message exists in the cache for a given query key.
   */
  const messageExistsInCache = useCallback(
    (messageId: string, queryKey: ReturnType<typeof createQueryKey.chatMessages>): boolean => {
      const data = queryClient.getQueryData<ChatMessagesInfiniteData>(queryKey);
      if (!data?.pages) return false;

      const allMessages = data.pages.flatMap((page) => page.data || []);
      return allMessages.some((msg) => msg.id === messageId);
    },
    [queryClient]
  );

  /**
   * Generate a unique optimistic ID for new messages.
   */
  const generateOptimisticId = useCallback(
    () => `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    []
  );

  /**
   * Track a pending optimistic message for deduplication.
   */
  const trackPendingMessage = useCallback(
    (optimisticId: string, content: string, chatMode: ChatMode) => {
      pendingOptimisticMessagesRef.current.set(optimisticId, {
        content,
        timestamp: Date.now(),
        chatMode,
      });
    },
    []
  );

  /**
   * Clear a pending optimistic message after it's been confirmed.
   */
  const clearPendingMessage = useCallback((optimisticId: string) => {
    pendingOptimisticMessagesRef.current.delete(optimisticId);
  }, []);

  /**
   * Find a matching pending optimistic message by content and chatMode.
   */
  const findMatchingPendingMessage = useCallback(
    (content: string, chatMode: ChatMode): string | null => {
      for (const [optId, optData] of pendingOptimisticMessagesRef.current.entries()) {
        if (
          optData.chatMode === chatMode &&
          optData.content === content &&
          Date.now() - optData.timestamp < 30000 // Within 30 seconds
        ) {
          return optId;
        }
      }
      return null;
    },
    []
  );

  /**
   * Track a pending optimistic reaction for deduplication.
   */
  const trackPendingReaction = useCallback(
    (messageId: string, code: string, action: 'add' | 'remove') => {
      const reactionKey = `${messageId}:${code}:${action}`;
      pendingOptimisticReactionsRef.current.set(reactionKey, {
        messageId,
        code,
        action,
        timestamp: Date.now(),
      });
      return reactionKey;
    },
    []
  );

  /**
   * Check if a reaction was our own (pending) and clear it.
   */
  const checkAndClearPendingReaction = useCallback(
    (messageId: string, code: string, action: 'add' | 'remove'): boolean => {
      const reactionKey = `${messageId}:${code}:${action}`;
      const wasOurs = pendingOptimisticReactionsRef.current.has(reactionKey);
      pendingOptimisticReactionsRef.current.delete(reactionKey);
      return wasOurs;
    },
    []
  );

  /**
   * Clear all pending messages for a specific chat mode.
   */
  const clearPendingMessagesForMode = useCallback((chatMode: ChatMode) => {
    for (const [key, data] of pendingOptimisticMessagesRef.current.entries()) {
      if (data.chatMode === chatMode) {
        pendingOptimisticMessagesRef.current.delete(key);
      }
    }
  }, []);

  /**
   * Clear all stale pending entries (older than threshold).
   */
  const clearStalePendingEntries = useCallback((staleThresholdMs = 30000) => {
    const now = Date.now();

    for (const [key, data] of pendingOptimisticMessagesRef.current.entries()) {
      if (now - data.timestamp > staleThresholdMs) {
        pendingOptimisticMessagesRef.current.delete(key);
      }
    }

    for (const [key, data] of pendingOptimisticReactionsRef.current.entries()) {
      if (now - data.timestamp > staleThresholdMs) {
        pendingOptimisticReactionsRef.current.delete(key);
      }
    }
  }, []);

  /**
   * Clear all pending entries (for unmount cleanup).
   */
  const clearAllPendingEntries = useCallback(() => {
    pendingOptimisticMessagesRef.current.clear();
    pendingOptimisticReactionsRef.current.clear();
  }, []);

  return {
    // Query client
    queryClient,

    // Refs
    pendingOptimisticMessagesRef,
    pendingOptimisticReactionsRef,
    expectEventRef,

    // Query keys
    messagesQueryKey,
    postsQueryKey,
    getQueryKeyForChatMode,

    // Cache utilities
    messageExistsInCache,

    // Message tracking
    generateOptimisticId,
    trackPendingMessage,
    clearPendingMessage,
    findMatchingPendingMessage,

    // Reaction tracking
    trackPendingReaction,
    checkAndClearPendingReaction,

    // Cleanup utilities
    clearPendingMessagesForMode,
    clearStalePendingEntries,
    clearAllPendingEntries,
  };
}

export type ContestChatUtils = ReturnType<typeof useContestChatUtils>;
